/* import CCM JSON */
import jsonCategories from './CCM/models/CCM_CATEGORIES.json'
import jsonFields from './CCM/models/CCM_FIELDS.json'
import jsonHuntCategoryMap from './CCM/models/CCM_HUNT_CATEGORY_MAP.json'
import jsonPivotFields from './CCM/models/CCM_PIVOT_FIELDS.json'
import { renderToolFieldMappings } from './CCM/models/CCM_TOOL_RENDER_FIELDS'
import { SupportedTools } from './interfaces'

/* CCM JSON interfaces */
export interface CCM_CATEGORIES {
  [category: string]: {
    ccm_fields: Array<string>
    keywords: Array<string>
    name: string
    sourcetypes: Array<string>
  }
}

export interface CCM_FIELDS {
  [field: string]: {
    name: string
    description: string
    permutations: Array<string>
  }
}

export interface CCM_PIVOT_FIELDS {
  [field: string]: {
    primary_fields: Array<string>
    secondary_fields: Array<string>
  }
}

/**
 * CCM_HUNT_CATEGORY_MAP also carries the canonical
 * (log_category × hunt_category) → fields availability matrix.
 * Each hunt_category entry lists the log_categories that can express it,
 * and the CCM fields each of those log_categories physically carries for
 * events in that hunt_category. Owned by the content team.
 */
export interface CCM_HUNT_CATEGORY_MAP {
  [huntCategory: string]: {
    name: string
    ccm_log_categories: { [logCategory: string]: string[] }
  }
}

export interface CCM_TOOL_RENDER_FIELDS {
  [key: string]: {
    [key: string]: string
  }
}

/* export CCM JSON interfaces */
export const categories: CCM_CATEGORIES = <CCM_CATEGORIES>jsonCategories
export const fields: CCM_FIELDS = <CCM_FIELDS>jsonFields
export const huntCategories: CCM_PIVOT_FIELDS = <CCM_PIVOT_FIELDS>(
  jsonPivotFields
)

export const huntCategoryMap: CCM_HUNT_CATEGORY_MAP = <CCM_HUNT_CATEGORY_MAP>(
  jsonHuntCategoryMap
)

export const toolTuningFields: CCM_TOOL_RENDER_FIELDS = renderToolFieldMappings

/**
 * Fields physically available in a (logCategory, huntCategory) pair.
 * Returns an empty set when the pair isn't declared.
 */
export function fieldsFor(
  logCategory: string,
  huntCategory: string,
): Set<string> {
  return new Set(
    huntCategoryMap[huntCategory]?.ccm_log_categories?.[logCategory] ?? [],
  )
}

/**
 * Log categories declared for a hunt category.
 */
export function logCategoriesFor(huntCategory: string): string[] {
  return Object.keys(huntCategoryMap[huntCategory]?.ccm_log_categories ?? {})
}

/**
 * Hunt categories where a field is physically carried by at least one log
 * category. Derived lazily and memoized.
 */
let _fieldToHuntCategoriesCache: Record<string, string[]> | undefined
export function huntCategoriesForField(field: string): string[] {
  if (!_fieldToHuntCategoriesCache) {
    const acc: Record<string, Set<string>> = {}
    for (const [huntCategory, entry] of Object.entries(huntCategoryMap)) {
      for (const fs of Object.values(entry.ccm_log_categories)) {
        for (const f of fs) {
          ;(acc[f] ??= new Set()).add(huntCategory)
        }
      }
    }
    _fieldToHuntCategoriesCache = Object.fromEntries(
      Object.entries(acc).map(([k, v]) => [k, [...v].sort()]),
    )
  }
  return _fieldToHuntCategoriesCache[field] ?? []
}
